import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { FileText, Search } from "lucide-react";
import { toast } from "sonner";
import { ManualInvoiceDialog } from "@/components/ManualInvoiceDialog";

/**
 * Two-step new-invoice flow for the Billing page:
 *   1. Pick a customer (typeahead over /customers).
 *   2. Delegate to the existing ManualInvoiceDialog with that customer.
 */
export function NewInvoiceButton({ onCreated, label = "New invoice", testId = "new-invoice",
                                  icon: Icon = FileText, variant = "default",
                                  Dialog = ManualInvoiceDialog, successMsg = "Invoice created" }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [customer, setCustomer] = useState(null);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!pickerOpen) return;
        let alive = true;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/customers", {
                    params: q ? { q, limit: 20 } : { limit: 20 },
                });
                if (alive) setResults(data || []);
            } catch {
                if (alive) setResults([]);
            } finally {
                if (alive) setLoading(false);
            }
        }, 200);
        return () => { alive = false; clearTimeout(t); };
    }, [q, pickerOpen]);

    const pick = (c) => {
        setCustomer(c);
        setPickerOpen(false);
        setInvoiceOpen(true);
    };

    return (
        <>
            <Button size="sm" variant={variant} onClick={() => setPickerOpen(true)}
                    data-testid={`${testId}-btn`}>
                <Icon className="w-3.5 h-3.5 mr-1" />{label}
            </Button>

            <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
                <SheetContent className="w-full sm:max-w-md"
                              data-testid={`${testId}-picker`}>
                    <SheetHeader>
                        <SheetTitle>Pick a customer</SheetTitle>
                        <SheetDescription>
                            Search by name, account #, or address.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-3">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute top-2.5 left-2.5 text-muted-foreground" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)}
                                   className="pl-8" autoFocus
                                   placeholder="Search customers..."
                                   data-testid={`${testId}-search`} />
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto border rounded-md divide-y">
                            {loading && (
                                <div className="p-4 text-sm text-muted-foreground">Loading…</div>
                            )}
                            {!loading && results.length === 0 && (
                                <div className="p-4 text-sm text-muted-foreground">
                                    No customers match.
                                </div>
                            )}
                            {results.map((c) => (
                                <button key={c.id} onClick={() => pick(c)}
                                        className="w-full text-left px-3 py-2.5 hover:bg-secondary/50 focus:bg-secondary/60 transition-colors"
                                        data-testid={`pick-customer-${c.id}`}>
                                    <div className="font-medium text-sm">{c.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {c.account_number} · {c.email || "no email"}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {customer && (
                <Dialog
                    open={invoiceOpen}
                    onOpenChange={(v) => { setInvoiceOpen(v); if (!v) setCustomer(null); }}
                    customer={customer}
                    onCreated={() => {
                        if (successMsg) toast.success(successMsg);
                        onCreated?.();
                    }}
                />
            )}
        </>
    );
}
